// Boards, views, fields and tasks (04 §Boards and tasks; 06 §Boards and tasks). M2-01
// contracts: handlers are M2-03. Part of the base plan (ADR-0016 §8), so `feature: core`.
// `tasks.propose` (agent change as an approval) was added by M2-03 with its handler.
import { z } from "zod";
import { Actor } from "../actor";
import { AgentWake, Approval, ApprovalCardInput, RulingConditionInput } from "../agent-rows";
import { GateRule } from "../approvals";
import {
  BoardColor,
  BoardDigestInterval,
  FieldAudience,
  FieldType,
  GateMode,
  MetricCadence,
  MetricDirection,
  MetricUnit,
  RunEffortRequest,
  TaskDependencyKind,
  ViewKind,
} from "../enums";
import { Event } from "../events";
import { boundedRecord, IsoDate, LIMITS, NonEmpty, Timestamp, text, Uuid } from "../primitives";
import { TaskMetricQuery } from "../rows";
import {
  BlockNoteDoc,
  Board,
  BoardSettings,
  ColumnGateView,
  FieldDef,
  FieldOptions,
  Space,
  StatusSet,
  Task,
  TaskAssignee,
  TaskChecklist,
  TaskChecklistItem,
  TaskDependency,
  TaskFieldValue,
  View,
  ViewFilter,
  ViewSort,
} from "../work-rows";
import { CompanyItemParams, CompanyParams, ImportResult, PageQuery, page } from "./common";
import { DirectionVerdict } from "./direction";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;
/**
 * TEMPLATES-T3/T4: an agent's write held to its policy (domain/boards/tasks.ts): Never answers
 * `policy_denied`; an Ask set by a policy row or the role's layers answers 409 to propose it.
 */
const AGENT_POLICY = ["policy_denied", "conflict"] as const;

// Boards ---------------------------------------------------------------------------------------

/**
 * WORKFORCE-BOARDVIEW (2026-10-26): one board with whether the caller may edit its settings
 * (`board#editor` on a live board), so the client offers "Set as board default" only then.
 */
export const BoardWithAccess = Board.extend({ can_edit: z.boolean().optional() }).meta({
  id: "BoardWithAccess",
});

export const BoardTemplateKey = z.enum([
  "blank",
  "kanban",
  "sprint",
  "content_calendar",
  "client_onboarding",
  // AWARENESS-MVP: Proposed · Approved · Rejected · Used; Why, Source, From, Kind, Used in
  "ideas",
  // BOARD-GATES: Research → … → Published, a person holds Ideas and Review (the default preset)
  "content_pipeline",
]);

export const BoardCreate = z
  .strictObject({
    space_id: Uuid,
    name: NonEmpty,
    template: BoardTemplateKey.optional().describe("Default `blank`"),
    status_set: StatusSet.optional().describe("Default: the space's default status set"),
    settings: BoardSettings.optional(),
    digest_interval: BoardDigestInterval.optional(),
    color: BoardColor.nullable().optional().describe("BOARD-ORDER: default null (tint by name)"),
    lead_seat_id: Uuid.nullable().optional().describe("BOARD-ORDER: a seat of the company"),
  })
  .meta({ id: "BoardCreate" });
export const BoardPatch = z
  .strictObject({
    name: NonEmpty.optional(),
    status_set: StatusSet.optional(),
    settings: BoardSettings.optional(),
    digest_interval: BoardDigestInterval.optional(),
    color: BoardColor.nullable().optional().describe("BOARD-ORDER: null: the tint by name"),
    lead_seat_id: Uuid.nullable().optional().describe("BOARD-ORDER: a seat of the company"),
  })
  .meta({ id: "BoardPatch" });
/**
 * BOARD-ORDER: puts a board before another board of a space (null: last), in its own space or
 * another one (Linear/ClickUp drag). The target space's live boards are renumbered 0..n-1.
 */
export const BoardMove = z
  .strictObject({
    space_id: Uuid.optional().describe("Default: the board's own space"),
    before_board_id: Uuid.nullable().describe("A live board of the target space; null: last"),
  })
  .meta({ id: "BoardMove" });
/**
 * B1-DE-RULES: the rule behind a rule firing's events in a task's Activity ("by Marketing
 * Director (rule: …)"), keyed by the firing (the events' `correlation_id`). Only rules the reader
 * sees are named.
 */
export const TaskHistoryRule = z
  .object({
    automation_id: Uuid,
    name: z.string(),
    /** Whom the firing acted as (the owning DE, or the owner person). */
    by_name: z.string().nullable(),
    by_de: z.boolean(),
  })
  .meta({ id: "TaskHistoryRule" });
export const TaskHistory = page(Event)
  .extend({ rules: z.record(z.string(), TaskHistoryRule).optional() })
  .meta({ id: "TaskHistory" });

/**
 * T6-BOARD-NOTE: a board's "How this board works" — the note (a versioned page, markdown in and
 * out), its owner, how often the owner checks the board, and the rules that run on it, rendered
 * from the `automation` rows (never retyped, so they cannot drift).
 */
export const BoardNoteRule = z
  .object({
    id: Uuid,
    name: z.string(),
    sentence: z.string().describe("The rule as one sentence"),
    enabled: z.boolean(),
    paused_reason: z.string().nullable(),
    fired_count: z.int().min(0),
    last_fired_at: Timestamp.nullable(),
    can_edit: z.boolean(),
    version: z.int().min(1),
    /** B1-DE-RULES: the DE that owns the rule (null: a person's). */
    owner_de_name: z.string().nullable().optional(),
  })
  .meta({ id: "BoardNoteRule" });
export const BoardNoteOwner = z
  .object({
    seat_id: Uuid,
    title: z.string(),
    /** Who holds the seat: a digital employee (DE), a person, or nobody yet. */
    holder: z.enum(["de", "person", "vacant"]),
    name: z.string().nullable(),
  })
  .meta({ id: "BoardNoteOwner" });
export const BoardNote = z
  .object({
    board_id: Uuid,
    board_name: z.string(),
    page_id: Uuid.nullable(),
    /** The note page's version; 0 while none is written. */
    version: z.int().min(0),
    body_markdown: z.string(),
    updated_at: Timestamp.nullable(),
    owner: BoardNoteOwner.nullable(),
    wake_window_minutes: z.int().min(5).max(1440),
    rules: z.array(BoardNoteRule),
    /** "Pause rules" holds: it paused at least one rule, and no rule of the board runs. */
    rules_paused: z.boolean(),
    can_edit: z.boolean(),
  })
  .meta({ id: "BoardNote" });
export const BoardNoteWrite = z
  .strictObject({
    body_markdown: z.string().max(LIMITS.longText),
    version: z
      .int()
      .min(0)
      .describe("The note version the change is based on (0: none written yet); 409 when stale"),
  })
  .meta({ id: "BoardNoteWrite" });
export const BoardRulesPause = z
  .strictObject({ paused: z.boolean().describe("true pauses every rule of the board") })
  .meta({ id: "BoardRulesPause" });

/**
 * T8-BOARD-NUMBERS (universal-task plan §(c) wave 3): a board's "Numbers" tab. The built-in counts
 * are read live from the tasks the caller may view; every live task metric of the board
 * (`metric_def.source = task`, `task_query.board_id` = the board) is a tile read from its
 * `task_metric_daily` snapshot (counted live until the first snapshot run), with the current
 * period, the one before and a short trend in the metric's cadence. Pending DE proposals of a
 * metric on the board (`task_metric` approvals) are listed so nobody adds the same one twice.
 */
export const BoardNumberPoint = z
  .object({
    period_key: z.string(),
    /** The period's first day (company time). */
    starts_on: IsoDate,
    value: z.number().nullable().describe("null: nothing to average in the period"),
  })
  .meta({ id: "BoardNumberPoint" });
export const BoardNumberTile = z
  .object({
    metric_def_id: Uuid,
    key: z.string(),
    name: z.string(),
    unit: MetricUnit,
    direction: MetricDirection,
    cadence: MetricCadence,
    task_query: TaskMetricQuery,
    /** How it is counted, as one sentence ("Counts done tasks by the day each is done, weekly"). */
    how: z.string(),
    current: z.number().nullable(),
    previous: z.number().nullable(),
    /** Oldest first, ending with the current period; only periods the snapshot covers whole. */
    trend: z.array(BoardNumberPoint),
    /** The last snapshot run; null: not counted by the job yet (the values are counted live). */
    computed_at: Timestamp.nullable(),
    /** Seats carrying it as a KPI: such a number is removed from its seats first. */
    seat_count: z.int().min(0),
    can_remove: z.boolean(),
  })
  .meta({ id: "BoardNumberTile" });
export const BoardNumberProposal = z
  .object({
    approval_id: Uuid,
    name: z.string(),
    proposed_by: z.string().nullable().describe("The proposing seat's title"),
    created_at: Timestamp,
  })
  .meta({ id: "BoardNumberProposal" });
export const BoardNumbers = z
  .object({
    board_id: Uuid,
    /** Today in company time. */
    today: IsoDate,
    counts: z.object({
      total: z.int().min(0),
      open: z.int().min(0),
      done: z.int().min(0),
      overdue: z.int().min(0),
      due_this_week: z.int().min(0).describe("Open tasks due from now to the week's end"),
    }),
    by_status: z.array(
      z.object({ key: z.string(), name: z.string(), done: z.boolean(), count: z.int().min(0) }),
    ),
    /** Tasks done per week, the last 8 weeks, oldest first (company weeks). */
    throughput: z.array(z.object({ starts_on: IsoDate, done: z.int().min(0) })),
    tiles: z.array(BoardNumberTile),
    proposals: z.array(BoardNumberProposal),
    /** The caller may add a number here (a person who edits the live board). */
    can_add: z.boolean(),
  })
  .meta({ id: "BoardNumbers" });
export const BoardNumberCreate = z
  .strictObject({
    name: z.string().trim().min(1).max(120),
    task_query: TaskMetricQuery.describe("Its board_id must be this board"),
    cadence: MetricCadence.default("weekly"),
    unit: MetricUnit.default("number"),
    direction: MetricDirection.default("gte"),
  })
  .meta({ id: "BoardNumberCreate" });
export const BoardNumberParams = z.object({ co: Uuid, id: Uuid, metric_id: Uuid });
export type BoardNumberTile = z.infer<typeof BoardNumberTile>;
export type BoardNumbers = z.infer<typeof BoardNumbers>;
export type BoardNumberCreate = z.infer<typeof BoardNumberCreate>;

/**
 * B2-GATES (universal-task plan §b.4; planned-outputs §3): a person's gates on a board's
 * transitions. Each row is the board default (no seat) or one person's own; a person's row wins
 * over the default for the tasks they own. `effective_mode` is the mode after the admin ceiling
 * (no `trust` on this board: a `trust` row reads as `hold`).
 */
export const BoardGateRow = z
  .object({
    id: Uuid,
    seat_id: Uuid.nullable().describe("The person's seat; null: the board default"),
    seat_title: z.string().nullable(),
    holder_name: z.string().nullable(),
    from_status: z.string().nullable().describe("null: from any status"),
    to_status: z.string(),
    mode: GateMode,
    effective_mode: GateMode,
    hold_minutes: z.int().min(5).max(10080),
    /** The caller's own gate (a seat it holds). */
    mine: z.boolean(),
    can_edit: z.boolean(),
  })
  .meta({ id: "BoardGateRow" });
export const BoardGates = z
  .object({
    board_id: Uuid,
    /** The admin ceiling: no `trust` on this board. */
    no_trust: z.boolean(),
    /** The caller may set the ceiling (a company admin). */
    can_set_ceiling: z.boolean(),
    /** The caller may set the board default (an editor of the board). */
    can_edit_default: z.boolean(),
    /** The seats the caller holds (the ones its own gates may name). */
    my_seat_ids: z.array(Uuid),
    gates: z.array(BoardGateRow),
  })
  .meta({ id: "BoardGates" });
export const BoardGatePut = z
  .strictObject({
    seat_id: Uuid.nullable().describe("A seat the caller holds; null: the board default"),
    from_status: z.string().max(LIMITS.key).nullable(),
    to_status: text(LIMITS.key),
    mode: GateMode,
    hold_minutes: z.int().min(5).max(10080).optional(),
  })
  .meta({ id: "BoardGatePut" });
export const BoardGateParams = z.object({ co: Uuid, id: Uuid, gate_id: Uuid });
export const BoardGateCeilingPut = z
  .strictObject({ no_trust: z.boolean().describe("true forbids `trust` on this board") })
  .meta({ id: "BoardGateCeilingPut" });
/**
 * B2-GATES: an agent's (the Chief of Staff's) proposal of a person's gates, as structured gates
 * or as the person's own sentence ("ask me before anything moves to Scheduled; hold Published
 * for 12 hours"), which the server reads. Filed as a `gate_change` for that person to confirm.
 */
export const BoardGateProposal = z
  .strictObject({
    seat_id: Uuid.describe("The person's seat whose gates these are"),
    gates: z.array(GateRule).max(20).optional(),
    sentence: z.string().max(1000).optional(),
    summary: z.string().max(LIMITS.text).optional(),
  })
  .meta({ id: "BoardGateProposal" });
export const BoardGateProposed = z
  .object({ approval_id: Uuid, approver_seat_id: Uuid, gates: z.array(GateRule) })
  .meta({ id: "BoardGateProposed" });
/** B2-GATES: a batch review over N tasks of a board (one approval per owner). */
export const BatchReviewCreate = z
  .strictObject({
    to_status: text(LIMITS.key).describe("The status every kept task moves to"),
    task_ids: z.array(Uuid).min(1).max(100),
    drop_status: z
      .string()
      .max(LIMITS.key)
      .nullable()
      .optional()
      .describe("Where a dropped task goes; absent: it stays, the reason noted"),
    summary: z.string().max(LIMITS.text).optional(),
    expires_at: Timestamp.nullable().optional(),
  })
  .meta({ id: "BatchReviewCreate" });
export const BatchReviewFiled = z
  .object({
    approvals: z.array(z.object({ id: Uuid, approver_seat_id: Uuid, count: z.int().min(1) })),
  })
  .meta({ id: "BatchReviewFiled" });
/** B2-GATES: a DE's move through a `hold` gate as its owner sees it. */
export const GateHoldView = z
  .object({
    id: Uuid,
    task_id: Uuid,
    board_id: Uuid,
    from_status: z.string(),
    to_status: z.string(),
    owner_seat_id: Uuid,
    held_by_membership_id: Uuid,
    held_by_name: z.string().nullable(),
    until: Timestamp,
    /** `passed`: open past `until` (the move stands). */
    state: z.enum(["open", "pulled", "passed"]),
    note: z.string().nullable(),
    can_pull: z.boolean(),
  })
  .meta({ id: "GateHoldView" });
export const TaskHold = z.object({ hold: GateHoldView.nullable() }).meta({ id: "TaskHold" });
export const GateHoldPull = z
  .strictObject({ note: z.string().max(2000).optional() })
  .meta({ id: "GateHoldPull" });

/**
 * BOARD-GATES (§2.2): the person-only gate of the column a task is in, as the caller sees it —
 * where Approve · Rework · Reject send it and whether the caller decides it. Null: the column has
 * none (or column gates are off).
 */
export const TaskReviewGate = z
  .object({
    gate: ColumnGateView.nullable(),
  })
  .meta({ id: "TaskReviewGate" });

/** BOARD-GATES (§2.2): Approve, Rework or Reject a card in a person-only column. */
export const TaskReviewDecide = z
  .strictObject({
    decision: z.enum(["approve", "rework", "reject"]),
    note: z
      .string()
      .max(2000)
      .optional()
      .describe("Required (at least 10 characters) on rework and reject; posted as a comment"),
    edits: z
      .strictObject({
        title: z.string().min(1).max(500).optional(),
        fields: boundedRecord(z.unknown(), LIMITS.list, Uuid)
          .optional()
          .describe("Field values by field id, set before the approval"),
      })
      .optional()
      .describe("Approve only: changes made before approving (recorded as approved with edits)"),
    wrong: z
      .boolean()
      .optional()
      .describe(
        "Rework or reject only: the delivered work was wrong (counted as a reversal on the agent's record)",
      ),
  })
  .meta({ id: "TaskReviewDecide" });

/** BOARD-ORDER: puts a space before another (null: last) in the company's order of spaces. */
export const SpaceMove = z
  .strictObject({
    before_space_id: Uuid.nullable().describe("A live shared space; null: last"),
  })
  .meta({ id: "SpaceMove" });
export const BoardListQuery = PageQuery.extend({
  space_id: Uuid.optional(),
  include_archived: z.enum(["true", "false"]).optional(),
});

export const ViewCreate = z
  .strictObject({
    kind: ViewKind,
    name: NonEmpty,
    filter: ViewFilter.optional(),
    sort: ViewSort.optional(),
    group_by: z.string().max(64).nullable().optional(),
    visible_fields: z.array(z.string().max(64)).max(100).optional(),
    /** T4-VIEWS-MEDIA: calendar / timeline date (`due_at`, `start_at`, `field:{uuid}`). */
    date_field: z.string().max(64).nullable().optional(),
    /** T4-VIEWS-MEDIA: the view's card fields; null: the show-on-card fallback. */
    card_fields: z.array(z.string().max(64)).max(12).nullable().optional(),
    is_default: z.boolean().optional(),
    position: z.int().optional(),
  })
  .meta({ id: "ViewCreate" });
export const ViewPatch = ViewCreate.omit({ kind: true }).partial().meta({ id: "ViewPatch" });

export const FieldCreate = z
  .strictObject({
    name: NonEmpty,
    type: FieldType,
    options: FieldOptions.optional(),
    audience: FieldAudience.optional().describe("Default `both`"),
    requires_approval: z.boolean().optional(),
    position: z.int().optional(),
  })
  .meta({ id: "FieldCreate" });
/** A field's type never changes; make a new field instead. */
export const FieldPatch = FieldCreate.omit({ type: true }).partial().meta({ id: "FieldPatch" });

export const BoardItemParams = z.object({ co: Uuid, id: Uuid, sub_id: Uuid });

/** A task as lists render it: its assignees and field values inline (one query, 04 budgets). */
export const TaskCard = Task.extend({
  assignee_seat_ids: z.array(Uuid),
  fields: z.record(z.string(), z.unknown()).describe("field_def id → value; audience-filtered"),
  checklist: z.object({ done: z.int().min(0), total: z.int().min(0) }),
  blocked: z.boolean().describe("An open hard dependency"),
  // WORK-OS: the timeline draws dependency arrows from the cards alone.
  depends_on_task_ids: z.array(Uuid).describe("Tasks this one depends on (hard or soft)"),
  // WOS-BUNDLE: chips on cards and rows, and the tag filter.
  tag_ids: z.array(Uuid).describe("The task's tags (`tags.list` names and colours them)"),
  // TASK-LOCK: the agent run working on the task now (absent or null: none). While it works,
  // nobody else moves, re-columns or reassigns the task (409 `task_locked`).
  agent_lock: z
    .object({
      run_id: Uuid,
      membership_id: Uuid,
      name: z.string(),
      since: Timestamp,
      can_stop: z
        .boolean()
        .describe("The caller may stop it (`tasks.stopAgent`): admins, managers, the owner"),
    })
    .nullable()
    .optional(),
}).meta({ id: "TaskCard" });

/**
 * WORK-OS: one board as the Work home overview and the sidebar read it (Asana portfolio /
 * ClickUp Everything): its counts over the tasks the caller may view, the next due date, the
 * seat that leads it (the space's team lead) and the seats most assigned on its open tasks.
 */
export const BoardSummary = z
  .object({
    board_id: Uuid,
    space_id: Uuid,
    name: z.string(),
    home_channel_id: Uuid.nullable(),
    archived_at: Timestamp.nullable(),
    lead_seat_id: Uuid.nullable().describe(
      "The board's lead seat (BOARD-ORDER), else the lead seat of the space's team, when it has one",
    ),
    color: BoardColor.nullable().describe("BOARD-ORDER: the board's tint; null: picked by name"),
    position: z.int().describe("BOARD-ORDER: the board's place in its space (lower first)"),
    assignee_seat_ids: z
      .array(Uuid)
      .describe("Seats on the board's open tasks, most tasks first (at most eight)"),
    total: z.int().min(0),
    done: z.int().min(0),
    open: z.int().min(0),
    overdue: z.int().min(0).describe("Open and due before now"),
    blocked: z.int().min(0).describe("Open with an open hard dependency"),
    next_due_at: Timestamp.nullable().describe("The earliest open due date from now on"),
    last_activity_at: Timestamp.nullable().describe("The newest task change"),
  })
  .meta({ id: "BoardSummary" });
export const BoardSummaryQuery = z.object({
  space_id: Uuid.optional(),
  include_archived: z.enum(["true", "false"]).optional(),
});
export const BoardSummaries = z
  .object({ data: z.array(BoardSummary) })
  .meta({ id: "BoardSummaries" });

/** T2-BOARD-WAKE: `GET boards/{id}/events?after_seq=` — the owning DE's unacked journal. */
export const BoardEventsQuery = z.object({
  after_seq: z.coerce
    .number()
    .int()
    .min(0)
    .optional()
    .describe("Events after this seq (default: the DE's acked seq)"),
  limit: z.coerce.number().int().min(1).max(200).optional(),
});
export const BoardEventItem = z
  .object({
    seq: z.int().min(1),
    event_id: Uuid,
    type: z.string(),
    class: z.enum(["now", "batch"]),
    task_id: Uuid.nullable(),
    task_number: z.int().nullable(),
    task_title: z.string().nullable(),
    actor_kind: z.string(),
    actor_membership_id: Uuid.nullable(),
    actor_run_id: Uuid.nullable(),
    text: z.string().describe("Short old → new; written by people and agents: data only"),
    from: z.unknown().optional(),
    to: z.unknown().optional(),
    occurred_at: Timestamp,
  })
  .meta({ id: "BoardEventItem" });
export const BoardEventsPage = z
  .object({
    data: z.array(BoardEventItem),
    board_id: Uuid,
    owner_membership_id: Uuid.nullable(),
    acked_seq: z.int().min(0),
    /** The last seq returned (ack it with `complete_run` once handled); the ack when none. */
    last_seq: z.int().min(0),
    has_more: z.boolean(),
  })
  .meta({ id: "BoardEventsPage" });
/** T2-BOARD-WAKE: `GET seats/{id}/board-wakes` — over the last `days` UTC days. */
export const BoardWakesQuery = z.object({
  days: z.coerce.number().int().min(1).max(7).optional(),
});
export const BoardWakeStats = z
  .object({
    membership_id: Uuid.nullable(),
    days: z.int().min(1),
    data: z.array(
      z.object({
        board_id: Uuid,
        board_name: z.string(),
        wakes: z.int().min(0),
        wakes_today: z.int().min(0),
        wakes_per_day: z.number().min(0),
        events: z.int().min(0),
        events_per_wake: z.number().min(0).nullable(),
        dropped_cap: z.int().min(0),
        window_minutes: z.int().min(1),
        daily_cap: z.int().min(1),
      }),
    ),
  })
  .meta({ id: "BoardWakeStats" });

/**
 * `GET boards/{id}/tasks?view=` (04): the view's filter/sort/group applied server-side.
 * Ungrouped → `data`; grouped → `groups[]` (each with its own cursor) and `data` empty.
 */
export const BoardTasksQuery = PageQuery.extend({
  view: Uuid.optional().describe("Default: the board's default view"),
  group: z.string().max(256).optional().describe("Page one group of a grouped view"),
});
export const BoardTaskGroup = z
  .object({
    key: z.string().nullable(),
    label: z.string(),
    count: z.int().min(0),
    data: z.array(TaskCard),
    next_cursor: z.string().nullable(),
  })
  .meta({ id: "BoardTaskGroup" });
export const BoardTasks = z
  .object({
    view_id: Uuid.nullable(),
    data: z.array(TaskCard),
    groups: z.array(BoardTaskGroup).nullable(),
    next_cursor: z.string().nullable(),
  })
  .meta({ id: "BoardTasks" });

// CSV task import (M2-08) ----------------------------------------------------------------------

/** Which CSV column feeds each task attribute and board field (`@companyos/domain-work/import`). */
export const TaskImportMapping = z
  .strictObject({
    columns: z
      .strictObject({
        title: z.string().min(1).max(200),
        description: z.string().min(1).max(200).optional(),
        status: z.string().min(1).max(200).optional(),
        priority: z.string().min(1).max(200).optional(),
        due_at: z.string().min(1).max(200).optional(),
        start_at: z.string().min(1).max(200).optional(),
        estimate_minutes: z.string().min(1).max(200).optional(),
        assignees: z.string().min(1).max(200).optional(),
        external_id: z.string().min(1).max(200).optional(),
      })
      .describe("Task attribute → CSV column (header name, compared normalised)"),
    fields: boundedRecord(z.string().min(1).max(200), 32, Uuid)
      .optional()
      .describe("Board field id → CSV column"),
  })
  .meta({ id: "TaskImportMapping" });

/** Multipart CSV upload of tasks onto one board; `dry_run=true` writes nothing. */
export const TaskImportUpload = z
  .object({
    file: z.string().describe("CSV file (multipart part `file`)").meta({ format: "binary" }),
    dry_run: z.enum(["true", "false"]).optional(),
    mapping: z
      .string()
      .max(16_384)
      .describe("`TaskImportMapping` as JSON (multipart part `mapping`)"),
  })
  .meta({ id: "TaskImportUpload" });

// Tasks ----------------------------------------------------------------------------------------

export const TaskCreate = z
  .strictObject({
    board_id: Uuid,
    client_id: z.string().min(1).max(128).optional().describe("Idempotent per company when given"),
    title: text(500).optional().describe("Required unless `template_id` gives one"),
    /**
     * B4-ITEM-TEMPLATES: one of the board's item templates fills the task; anything this body
     * names itself wins (`origin` `template`, `origin_ref.item_template_id`).
     */
    template_id: Uuid.optional().describe("An item template of the board to fill the task from"),
    status: z.string().max(64).optional().describe("Default: the board's first status"),
    priority: z.int().min(0).max(4).optional(),
    parent_task_id: Uuid.nullable().optional(),
    due_at: Timestamp.nullable().optional(),
    start_at: Timestamp.nullable().optional(),
    estimate_minutes: z.int().min(0).nullable().optional(),
    assignee_seat_ids: z.array(Uuid).max(20).optional(),
    fields: boundedRecord(z.unknown(), LIMITS.list, Uuid).optional(),
    body: BlockNoteDoc.optional(),
    body_markdown: z.string().max(200_000).optional(),
    checklist: z.array(text(500)).max(200).optional(),
  })
  .refine((b) => b.title !== undefined || b.template_id !== undefined, {
    message: "title is required (or a template_id that gives one)",
    path: ["title"],
  })
  .meta({ id: "TaskCreate" });
export const TaskPatch = z
  .strictObject({
    title: text(500).optional(),
    priority: z.int().min(0).max(4).optional(),
    parent_task_id: Uuid.nullable().optional(),
    due_at: Timestamp.nullable().optional(),
    start_at: Timestamp.nullable().optional(),
    estimate_minutes: z.int().min(0).nullable().optional(),
  })
  .meta({ id: "TaskPatch" });

export const TaskListQuery = PageQuery.extend({
  board_id: Uuid.optional(),
  assignee_seat_id: Uuid.optional(),
  status: z.string().max(64).optional(),
  parent_task_id: Uuid.optional(),
});

/** A task with what the task page shows besides its body page. */
export const TaskDetail = TaskCard.extend({
  /** DOCS-KB: the task's checklists in order; `checklist_items` is the same items, flat. */
  checklists: z.array(TaskChecklist),
  checklist_items: z.array(TaskChecklistItem),
  dependencies: z.array(TaskDependency),
  field_values: z.array(TaskFieldValue),
  assignees: z.array(TaskAssignee),
}).meta({ id: "TaskDetail" });

/**
 * Parity P1 (wave 0 task detail assembler, services/api domain/boards/task-detail.ts): what
 * packages add to `GET tasks/{id}` and `get_task`, one key per registered extension (snake_case;
 * `get_task` serves it camelCased). Each key's shape is its package's contract; a key the
 * caller may not see is absent, never null-filled.
 */
export const TaskDetailExtensions = z
  .record(z.string().regex(/^[a-z][a-z0-9_]{0,63}$/), z.unknown())
  .meta({ id: "TaskDetailExtensions" });

/** `GET tasks/{id}`: the detail and the registered extensions (always present from the API). */
export const TaskDetailRead = TaskDetail.extend({
  extensions: TaskDetailExtensions.optional(),
}).meta({ id: "TaskDetailRead" });

/** Parity P1: the undo record a reversible write left (`POST undo/{id}`); null when nothing changed. */
const undoId = Uuid.nullable().describe(
  "Parity P1: `POST undo/{id}` reverses this write within 15 minutes; null when it changed nothing",
);
/** A task write's answer: the task and, Parity P1, the write's undo record. */
export const TaskWrite = TaskDetail.extend({
  undo_id: undoId,
  /** COS-MODES: an agent's routed priority or deadline change (`ask`: not changed yet). */
  direction: DirectionVerdict.optional(),
}).meta({ id: "TaskWrite" });
/**
 * CARD-ARCHIVE: a card's archive state after `tasks.archive` / `tasks.unarchive`, with what the
 * archive stopped (zeros for an unarchive, or an archive of a card already archived).
 */
export const CardArchiveState = z
  .object({
    task_id: Uuid,
    board_id: Uuid,
    number: z.int().min(1),
    archived_at: Timestamp.nullable().describe("Null: the card is not archived"),
    archived_by: Uuid.nullable().describe(
      "The membership that archived it (null: not archived, or an agency operator did)",
    ),
    runs_stopped: z.int().min(0),
    wakes_dropped: z.int().min(0),
    timers_cancelled: z.int().min(0),
    incidents_closed: z.int().min(0),
  })
  .meta({ id: "CardArchiveState" });
export type CardArchiveState = z.infer<typeof CardArchiveState>;
/** A field write's answer: the value and, Parity P1, the write's undo record. */
export const TaskFieldWrite = TaskFieldValue.extend({ undo_id: undoId }).meta({
  id: "TaskFieldWrite",
});

export const TaskMove = z
  .strictObject({
    board_id: Uuid,
    status: z.string().max(64).optional(),
    /** CREATE-TASK-FIELDS: values of the target board's fields, written before its gates check. */
    fields: boundedRecord(z.unknown(), LIMITS.list, Uuid)
      .optional()
      .describe("The target board's field values (by field id), set as the task lands"),
  })
  .meta({ id: "TaskMove" });
export const TaskStatusSet = z
  .strictObject({ status: z.string().min(1).max(64) })
  .meta({ id: "TaskStatusSet" });
export const TaskAssign = z
  .strictObject({
    seat_ids: z.array(Uuid).min(1).max(20),
    handoff: z
      .strictObject({
        from_seat_id: Uuid.describe("The seat handing the task on: it leaves the assignees"),
        reason: z.string().max(2000).nullable().optional(),
      })
      .optional()
      .describe(
        "HANDOFF-LOG: a handoff: the seats are assigned, `from_seat_id` is removed, and one `task.handed_off` records who handed it to whom and why",
      ),
    urgent: z
      .strictObject({ reason: z.string().trim().min(3).max(500) })
      .optional()
      .describe(
        "COS-MODES: an agent's urgent order (a deadline today, a blocked CEO priority): always act-and-inform, Ask me first never applies; the reason is required; 3 a day per agent",
      ),
    effort: RunEffortRequest.optional().describe(
      "RUN-EFFORT: how hard a newly assigned DE's run works — low | medium | high, or auto (the default)",
    ),
  })
  .meta({ id: "TaskAssign" });
/** COS-MODES: the task, and for an agent caller the routing verdict (`ask`: nothing assigned yet). */
export const TaskAssignResult = TaskDetail.extend({
  direction: DirectionVerdict.optional(),
}).meta({ id: "TaskAssignResult" });
/**
 * AGENT-MENTION (05 §Delegation; 06 §Boards and tasks): one row per digital employee holding a
 * seat the task is assigned to — the newest wake-up the task gave it, or, when it gave none, why
 * not (`not_live`, `paused`; assigned in `backlog`: `queued` with that reason).
 */
export const TaskAgentWakes = z
  .object({ data: z.array(AgentWake.extend({ seat_id: Uuid })) })
  .meta({ id: "TaskAgentWakes" });
export const TaskFieldParams = z.object({ co: Uuid, id: Uuid, field_id: Uuid });
export const TaskSeatParams = z.object({ co: Uuid, id: Uuid, seat_id: Uuid });
export const TaskSubParams = z.object({ co: Uuid, id: Uuid, item_id: Uuid });
export const TaskFieldPut = z.strictObject({ value: z.unknown() }).meta({ id: "TaskFieldPut" });

export const ChecklistItemCreate = z
  .strictObject({
    text: text(500),
    position: z.int().optional(),
    checklist_id: Uuid.optional().describe(
      'Default: the task\'s first checklist (made as "Checklist" when it has none)',
    ),
    assignee_seat_id: Uuid.nullable().optional(),
    due_at: Timestamp.nullable().optional(),
  })
  .meta({ id: "ChecklistItemCreate" });
export const ChecklistItemPatch = z
  .strictObject({
    text: text(500).optional(),
    done: z.boolean().optional(),
    position: z.int().optional(),
    checklist_id: Uuid.optional().describe("Move the item to another checklist of the task"),
    assignee_seat_id: Uuid.nullable().optional(),
    due_at: Timestamp.nullable().optional(),
  })
  .meta({ id: "ChecklistItemPatch" });

// DOCS-KB: named checklists (several per task), renamed, reordered and deleted as a whole.
export const TaskChecklistParams = z.object({ co: Uuid, id: Uuid, checklist_id: Uuid });
export const ChecklistCreate = z
  .strictObject({
    title: text(200).optional().describe('Default "Checklist"'),
    items: z.array(text(500)).max(200).optional(),
    position: z.int().optional(),
  })
  .meta({ id: "ChecklistCreate" });
export const ChecklistPatch = z
  .strictObject({ title: text(200).optional(), position: z.int().optional() })
  .meta({ id: "ChecklistPatch" });
/** Every item of the checklist, in the order wanted (an item left out keeps its place after). */
export const ChecklistReorder = z
  .strictObject({ item_ids: z.array(Uuid).min(1).max(200) })
  .meta({ id: "ChecklistReorder" });
export const TaskChecklistWithItems = TaskChecklist.extend({
  items: z.array(TaskChecklistItem),
}).meta({ id: "TaskChecklistWithItems" });
export type TaskChecklistWithItems = z.infer<typeof TaskChecklistWithItems>;

export const DependencyAdd = z
  .strictObject({
    depends_on_task_id: Uuid,
    kind: TaskDependencyKind.optional().describe("Default `hard`"),
  })
  .meta({ id: "DependencyAdd" });

export const TaskThread = z.object({ thread_id: Uuid }).meta({ id: "TaskThread" });

export const TaskBulk = z
  .strictObject({
    task_ids: z.array(Uuid).min(1).max(200),
    action: z.discriminatedUnion("kind", [
      z.strictObject({ kind: z.literal("status"), status: z.string().min(1).max(64) }),
      z.strictObject({ kind: z.literal("assign"), seat_ids: z.array(Uuid).min(1).max(20) }),
      z.strictObject({ kind: z.literal("move"), board_id: Uuid }),
      z.strictObject({ kind: z.literal("field"), field_id: Uuid, value: z.unknown() }),
      z.strictObject({ kind: z.literal("delete") }),
      // WOS-BUNDLE: tag or untag many tasks at once.
      z.strictObject({ kind: z.literal("tag"), tag_id: Uuid, op: z.enum(["add", "remove"]) }),
    ]),
  })
  .meta({ id: "TaskBulk" });
export const TaskBulkResult = z
  .object({
    results: z.array(z.object({ task_id: Uuid, ok: z.boolean(), code: z.string().nullable() })),
  })
  .meta({ id: "TaskBulkResult" });

export const TaskSearchInclude = z
  .enum(["brief", "numbers", "links", "last_status_change"])
  .meta({ id: "TaskSearchInclude" });

/** `POST tasks/search` (04): full text over title/body plus field filters; a read. */
export const TaskSearch = z
  .strictObject({
    query: z.string().max(500).optional(),
    filters: ViewFilter.optional(),
    board_ids: z.array(Uuid).max(50).optional(),
    // T5-TASK-SEARCH: what each hit carries beyond its card (each one more statement per page).
    include: z
      .array(TaskSearchInclude)
      .max(4)
      .optional()
      .describe(
        "brief: the description's first lines; numbers: the latest live observation per metric; links: related, duplicate, dependency and parent tasks the caller can view; last_status_change: the newest status change or move, with its actor",
      ),
    cursor: z.string().max(1024).optional(),
    limit: z.int().min(1).max(200).optional(),
  })
  .meta({ id: "TaskSearch" });

/** T5-TASK-SEARCH: a task linked to a search hit (only tasks the caller can view). */
export const TaskSearchLink = z
  .object({
    kind: z.enum([
      "relates_to",
      "duplicates",
      "duplicated_by",
      "depends_on",
      "blocks",
      "parent",
      "child",
    ]),
    task_id: Uuid,
    board_id: Uuid,
    number: z.int(),
    title: z.string(),
  })
  .meta({ id: "TaskSearchLink" });

/** T5-TASK-SEARCH: one search hit — the task's card plus what `include` asked for. */
export const TaskSearchHit = TaskCard.extend({
  brief: z.string().nullable().optional().describe("The description's first lines (≤ 280 chars)"),
  numbers: z
    .array(
      z.object({
        metric: z.string(),
        value: z.number(),
        unit: z.string().nullable(),
        observed_at: Timestamp,
      }),
    )
    .optional()
    .describe("The latest live observation of each metric on the task"),
  links: z.array(TaskSearchLink).optional(),
  last_status_change: z
    .object({
      from: z.string().nullable().describe("null for a move to another board"),
      to: z.string(),
      board_id: Uuid,
      at: Timestamp,
      actor: Actor,
    })
    .nullable()
    .optional(),
}).meta({ id: "TaskSearchHit" });

/**
 * `POST tasks/{id}/propose` (04; M2-03): a change submitted as an approval instead of applied
 * (agents; anyone acting for a seat). The approval is `task_change`, subject the task; the
 * approver decides it with the approval routes (M3-03), whose executor applies the change.
 */
export const TaskChange = z
  .strictObject({
    status: z.string().min(1).max(64).optional(),
    title: text(500).optional(),
    priority: z.int().min(0).max(4).optional(),
    due_at: Timestamp.nullable().optional(),
    assignee_seat_ids: z.array(Uuid).max(20).optional(),
    fields: boundedRecord(z.unknown(), LIMITS.list, Uuid).optional(),
  })
  .meta({ id: "TaskChange" });
export const TaskPropose = z
  .strictObject({
    change: TaskChange,
    summary: z.string().max(LIMITS.text).optional(),
    requested_by_seat_id: Uuid.optional().describe("Default: the caller's (or its key's) seat"),
    approver_seat_id: Uuid.optional().describe(
      "A person's choice (default: the requesting seat's supervisor, else the first assignee's supervisor); ignored for an agent, whose approver the server derives (the owner's ask gate, a crew's owner, else the task's owner)",
    ),
    expires_at: Timestamp.nullable().optional(),
    card: ApprovalCardInput.optional().describe(
      "CORE-CARDS (flag `escalation_cards`; ignored while off): the escalation card",
    ),
    condition: RulingConditionInput.optional().describe(
      "CORE-PRECEDENTS (flag `precedents`; ignored while off): the case's structured facts, matched against the company's rulings",
    ),
  })
  .meta({ id: "TaskPropose" });

export const MyTasksQuery = PageQuery.extend({
  include_done: z.enum(["true", "false"]).optional(),
  due_before: Timestamp.optional(),
});

// Routes ----------------------------------------------------------------------------------------

const boardSub = (path: string) => `${base}/boards/{id}/${path}`;
const taskSub = (path: string) => `${base}/tasks/{id}/${path}`;

export const workRoutes = {
  "boards.list": defineRoute({
    ...core,
    id: "boards.list",
    method: "get",
    path: `${base}/boards`,
    tag: "boards",
    summary: "Boards the caller can view (optionally of one space)",
    access: "member",
    params: CompanyParams,
    query: BoardListQuery,
    response: { status: 200, schema: page(Board) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "boards.create": defineRoute({
    ...core,
    id: "boards.create",
    method: "post",
    path: `${base}/boards`,
    tag: "boards",
    summary: "Create a board (with its home channel and default view)",
    access: "member",
    params: CompanyParams,
    body: BoardCreate,
    idempotency: "optional",
    response: { status: 201, schema: Board },
    errors: [],
    emits: ["board.created", "channel.created", "view.created", "field_def.created"],
  }),
  "boards.summary": defineRoute({
    ...core,
    id: "boards.summary",
    method: "get",
    path: `${base}/boards/summary`,
    tag: "boards",
    summary: "Every board the caller can view with its task counts, next due date and people",
    access: "member",
    params: CompanyParams,
    query: BoardSummaryQuery,
    response: { status: 200, schema: BoardSummaries },
    errors: [],
    emits: [],
  }),
  "boards.get": defineRoute({
    ...core,
    id: "boards.get",
    method: "get",
    path: `${base}/boards/{id}`,
    tag: "boards",
    summary: "Get a board",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: BoardWithAccess },
    errors: [],
    emits: [],
  }),
  "boards.events": defineRoute({
    ...core,
    id: "boards.events",
    method: "get",
    path: `${base}/boards/{id}/events`,
    tag: "boards",
    summary:
      "T2-BOARD-WAKE: what happened on a board its owning DE has not acked (the DE's own journal)",
    access: "member",
    params: CompanyItemParams,
    query: BoardEventsQuery,
    response: { status: 200, schema: BoardEventsPage },
    errors: ["forbidden"],
    emits: [],
  }),
  "seats.board_wakes": defineRoute({
    ...core,
    id: "seats.board_wakes",
    method: "get",
    path: `${base}/seats/{id}/board-wakes`,
    tag: "boards",
    summary: "T2-BOARD-WAKE: the board wakes of the DE on a seat (wakes a day, events a wake)",
    access: "member",
    params: CompanyItemParams,
    query: BoardWakesQuery,
    response: { status: 200, schema: BoardWakeStats },
    errors: [],
    emits: [],
  }),
  "boards.update": defineRoute({
    ...core,
    id: "boards.update",
    method: "patch",
    path: `${base}/boards/{id}`,
    tag: "boards",
    summary: "Rename a board or change its statuses, settings or digest",
    access: "member",
    params: CompanyItemParams,
    body: BoardPatch,
    ifMatch: true,
    response: { status: 200, schema: Board },
    errors: ["status_in_use"],
    emits: ["board.updated"],
  }),
  "boards.archive": defineRoute({
    ...core,
    id: "boards.archive",
    method: "post",
    path: `${base}/boards/{id}/archive`,
    tag: "boards",
    summary: "Archive a board and its home channel",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: Board },
    errors: ["conflict"],
    emits: ["board.archived", "channel.archived"],
  }),
  // ACTIONS: back from the archive (in its old place) with the home channel its archive took.
  "boards.unarchive": defineRoute({
    ...core,
    id: "boards.unarchive",
    method: "post",
    path: `${base}/boards/{id}/unarchive`,
    tag: "boards",
    summary: "Restore an archived board and its home channel (its space must be live)",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: Board },
    errors: ["conflict"],
    emits: ["board.unarchived", "channel.restored"],
  }),
  "boards.move": defineRoute({
    ...core,
    id: "boards.move",
    method: "post",
    path: `${base}/boards/{id}/move`,
    tag: "boards",
    summary: "Reorder a board within its space or move it to another space (BOARD-ORDER)",
    access: "member",
    params: CompanyItemParams,
    body: BoardMove,
    idempotency: "optional",
    response: { status: 200, schema: Board },
    errors: ["conflict"],
    emits: ["board.moved"],
  }),
  // T6-BOARD-NOTE: the board's "How this board works" (note, owner, check window, rules).
  "boards.note": defineRoute({
    ...core,
    id: "boards.note",
    method: "get",
    path: `${base}/boards/{id}/note`,
    tag: "boards",
    summary: "A board's “How this board works”: its note, owner, check window and rules",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: BoardNote },
    errors: [],
    emits: [],
  }),
  "boards.writeNote": defineRoute({
    ...core,
    id: "boards.writeNote",
    method: "put",
    path: `${base}/boards/{id}/note`,
    tag: "boards",
    summary:
      "Write a board's note (a new page version; the first write creates the page). A DE's write is held to its policy (`board_config`): an Ask answers 409 to propose it",
    access: "member",
    params: CompanyItemParams,
    body: BoardNoteWrite,
    idempotency: "optional",
    response: { status: 200, schema: BoardNote },
    errors: [...AGENT_POLICY],
    emits: ["page.created", "page.edited", "board.updated", "wakeup.requested"],
  }),
  "boards.pauseRules": defineRoute({
    ...core,
    id: "boards.pauseRules",
    method: "post",
    path: `${base}/boards/{id}/rules/pause`,
    tag: "boards",
    summary: "Pause every rule of a board, or resume the ones “Pause rules” paused",
    access: "member",
    params: CompanyItemParams,
    body: BoardRulesPause,
    idempotency: "optional",
    response: { status: 200, schema: BoardNote },
    errors: [],
    emits: ["automation.paused", "automation.updated"],
  }),
  // T8-BOARD-NUMBERS: the board's Numbers tab (counts, status breakdown, throughput, tiles).
  "boards.numbers": defineRoute({
    ...core,
    id: "boards.numbers",
    method: "get",
    path: `${base}/boards/{id}/numbers`,
    tag: "boards",
    summary:
      "A board's numbers: open / done / overdue / due this week, tasks per status, tasks done per week, and each task metric of the board as a tile with its trend",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: BoardNumbers },
    errors: [],
    emits: [],
  }),
  "boards.addNumber": defineRoute({
    feature: "kpi",
    id: "boards.addNumber",
    method: "post",
    path: `${base}/boards/{id}/numbers`,
    tag: "boards",
    summary:
      "Add a number to a board: a task metric counted from its tasks (a person who edits the board; a DE proposes one with propose_task_metric)",
    access: "member",
    params: CompanyItemParams,
    body: BoardNumberCreate,
    idempotency: "optional",
    response: { status: 201, schema: BoardNumbers },
    errors: ["forbidden", "conflict"],
    emits: ["metric_def.created"],
  }),
  "boards.removeNumber": defineRoute({
    feature: "kpi",
    id: "boards.removeNumber",
    method: "delete",
    path: `${base}/boards/{id}/numbers/{metric_id}`,
    tag: "boards",
    summary:
      "Remove a number from a board (its metric is archived); a number that is a seat's KPI is taken off its seats first",
    access: "member",
    params: BoardNumberParams,
    response: { status: 200, schema: BoardNumbers },
    errors: ["forbidden", "conflict"],
    emits: ["metric_def.archived"],
  }),
  // B2-GATES: a person's gates on a board's transitions (ask / hold / trust), the admin ceiling,
  // an agent's proposal of a person's gates, and a batch review over N tasks.
  "boards.gates": defineRoute({
    ...core,
    id: "boards.gates",
    method: "get",
    path: `${base}/boards/{id}/gates`,
    tag: "boards",
    summary:
      "A board's gates: the board default and the caller's own (an admin sees everyone's), each with its effective mode",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: BoardGates },
    errors: [],
    emits: [],
  }),
  "boards.putGate": defineRoute({
    ...core,
    id: "boards.putGate",
    method: "put",
    path: `${base}/boards/{id}/gates`,
    tag: "boards",
    summary:
      "Set a gate on a transition: the caller's own (a seat it holds) or the board default (an editor). Agents propose instead",
    access: "member",
    params: CompanyItemParams,
    body: BoardGatePut,
    idempotency: "optional",
    response: { status: 200, schema: BoardGates },
    errors: ["forbidden", "status_invalid"],
    emits: ["board.gates_changed"],
  }),
  "boards.deleteGate": defineRoute({
    ...core,
    id: "boards.deleteGate",
    method: "delete",
    path: `${base}/boards/{id}/gates/{gate_id}`,
    tag: "boards",
    summary: "Remove a gate (the caller's own, or the board default as an editor)",
    access: "member",
    params: BoardGateParams,
    response: { status: 200, schema: BoardGates },
    errors: ["forbidden"],
    emits: ["board.gates_changed"],
  }),
  "boards.gateCeiling": defineRoute({
    ...core,
    id: "boards.gateCeiling",
    method: "put",
    path: `${base}/boards/{id}/gates/ceiling`,
    tag: "boards",
    summary: "The company ceiling on a board: a company admin forbids `trust` there",
    access: "member",
    params: CompanyItemParams,
    body: BoardGateCeilingPut,
    idempotency: "optional",
    response: { status: 200, schema: BoardGates },
    errors: ["forbidden"],
    emits: ["board.gates_changed"],
  }),
  "boards.proposeGates": defineRoute({
    ...core,
    id: "boards.proposeGates",
    method: "post",
    path: `${base}/boards/{id}/gates/propose`,
    tag: "boards",
    summary:
      "An agent proposes a person's gates (structured, or read from the person's sentence); filed as a `gate_change` the person confirms",
    access: "member",
    params: CompanyItemParams,
    body: BoardGateProposal,
    idempotency: "optional",
    response: { status: 201, schema: BoardGateProposed },
    errors: ["forbidden", "status_invalid"],
    emits: ["approval.requested"],
  }),
  "boards.batchReview": defineRoute({
    ...core,
    id: "boards.batchReview",
    method: "post",
    path: `${base}/boards/{id}/batch-reviews`,
    tag: "boards",
    summary:
      "File one review over N tasks of a board (one `task_batch` approval per owner): keep / edit / send back / drop per row",
    access: "member",
    params: CompanyItemParams,
    body: BatchReviewCreate,
    idempotency: "optional",
    response: { status: 201, schema: BatchReviewFiled },
    errors: ["status_invalid", "forbidden", ...AGENT_POLICY],
    emits: ["approval.requested"],
  }),
  "spaces.move": defineRoute({
    ...core,
    id: "spaces.move",
    method: "post",
    path: `${base}/spaces/{id}/move`,
    tag: "spaces",
    summary: "Reorder a space in the company's order of spaces (company admins; BOARD-ORDER)",
    access: "member",
    params: CompanyItemParams,
    body: SpaceMove,
    idempotency: "optional",
    response: { status: 200, schema: Space },
    errors: ["conflict"],
    emits: ["space.moved"],
  }),
  "boards.tasks": defineRoute({
    ...core,
    id: "boards.tasks",
    method: "get",
    path: boardSub("tasks"),
    tag: "boards",
    summary: "Tasks through a view (filter, sort and group server-side; `groups[]` when grouped)",
    access: "member",
    params: CompanyItemParams,
    query: BoardTasksQuery,
    response: { status: 200, schema: BoardTasks },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "views.list": defineRoute({
    ...core,
    id: "views.list",
    method: "get",
    path: boardSub("views"),
    tag: "boards",
    summary: "Views of a board",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: z.object({ data: z.array(View) }) },
    errors: [],
    emits: [],
  }),
  "views.create": defineRoute({
    ...core,
    id: "views.create",
    method: "post",
    path: boardSub("views"),
    tag: "boards",
    summary: "Save a view",
    access: "member",
    params: CompanyItemParams,
    body: ViewCreate,
    idempotency: "optional",
    response: { status: 201, schema: View },
    errors: [],
    emits: ["view.created"],
  }),
  "views.update": defineRoute({
    ...core,
    id: "views.update",
    method: "patch",
    path: boardSub("views/{sub_id}"),
    tag: "boards",
    summary: "Change a saved view",
    access: "member",
    params: BoardItemParams,
    body: ViewPatch,
    response: { status: 200, schema: View },
    errors: [],
    emits: ["view.updated"],
  }),
  "views.delete": defineRoute({
    ...core,
    id: "views.delete",
    method: "delete",
    path: boardSub("views/{sub_id}"),
    tag: "boards",
    summary: "Delete a view (not the board's last one)",
    access: "member",
    params: BoardItemParams,
    response: { status: 204 },
    errors: ["conflict"],
    emits: ["view.deleted"],
  }),
  "fields.list": defineRoute({
    ...core,
    id: "fields.list",
    method: "get",
    path: boardSub("fields"),
    tag: "boards",
    summary: "Fields of a board (audience-filtered)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: z.object({ data: z.array(FieldDef) }) },
    errors: [],
    emits: [],
  }),
  "fields.create": defineRoute({
    ...core,
    id: "fields.create",
    method: "post",
    path: boardSub("fields"),
    tag: "boards",
    summary: "Add a field to a board",
    access: "member",
    params: CompanyItemParams,
    body: FieldCreate,
    idempotency: "optional",
    response: { status: 201, schema: FieldDef },
    errors: ["conflict"],
    emits: ["field_def.created"],
  }),
  "fields.update": defineRoute({
    ...core,
    id: "fields.update",
    method: "patch",
    path: boardSub("fields/{sub_id}"),
    tag: "boards",
    summary: "Rename a field or change its options, audience or approval rule",
    access: "member",
    params: BoardItemParams,
    body: FieldPatch,
    response: { status: 200, schema: FieldDef },
    errors: ["conflict"],
    emits: ["field_def.updated"],
  }),
  "fields.delete": defineRoute({
    ...core,
    id: "fields.delete",
    method: "delete",
    path: boardSub("fields/{sub_id}"),
    tag: "boards",
    summary: "Delete a field and its values",
    access: "member",
    params: BoardItemParams,
    response: { status: 204 },
    errors: [],
    emits: ["field_def.deleted"],
  }),

  "tasks.list": defineRoute({
    ...core,
    id: "tasks.list",
    method: "get",
    path: `${base}/tasks`,
    tag: "tasks",
    summary: "Tasks the caller can view (filter by board, assignee, status, parent)",
    access: "member",
    params: CompanyParams,
    query: TaskListQuery,
    response: { status: 200, schema: page(TaskCard) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "tasks.create": defineRoute({
    ...core,
    id: "tasks.create",
    method: "post",
    path: `${base}/tasks`,
    tag: "tasks",
    summary: "Create a task (idempotent on `client_id` when given)",
    access: "member",
    params: CompanyParams,
    body: TaskCreate,
    idempotency: "optional",
    // COS-MODES: an agent creating onto another person's agent gets the routing verdict too.
    response: { status: 201, schema: TaskAssignResult },
    // COS-SLICE1: an agent run past its effects cap.
    // TEMPLATES-T3/T4: an agent's create held to its policy (Never: policy_denied; Ask: 409).
    errors: [
      "status_invalid",
      "effect_cap_reached",
      "policy_denied",
      "direction_refused",
      "conflict",
    ],
    // M7-02: a given body becomes the task's page (`page.created`, kind `task_body`).
    // B4-ITEM-TEMPLATES: a template's tags (`task.tagged`)
    emits: [
      "task.created",
      "page.created",
      "task.agent_directed",
      "approval.requested",
      "task.tagged",
    ],
  }),
  "tasks.get": defineRoute({
    ...core,
    id: "tasks.get",
    method: "get",
    path: `${base}/tasks/{id}`,
    tag: "tasks",
    summary: "Get a task with checklist, dependencies, fields and assignees",
    access: "member",
    params: CompanyItemParams,
    // Parity P1: plus the task detail assembler's extensions
    response: { status: 200, schema: TaskDetailRead },
    errors: [],
    emits: [],
  }),
  "tasks.update": defineRoute({
    ...core,
    id: "tasks.update",
    method: "patch",
    path: `${base}/tasks/{id}`,
    tag: "tasks",
    summary: "Change a task's title, priority, parent, dates or estimate",
    access: "member",
    params: CompanyItemParams,
    body: TaskPatch,
    ifMatch: true,
    response: { status: 200, schema: TaskWrite },
    errors: ["direction_refused"],
    emits: [
      "task.updated",
      "task.due_changed",
      "task.priority_changed",
      "task.agent_directed",
      "approval.requested",
    ],
  }),
  "tasks.delete": defineRoute({
    ...core,
    id: "tasks.delete",
    method: "delete",
    path: `${base}/tasks/{id}`,
    tag: "tasks",
    summary: "Delete a task (and its subtasks' parent link; its body page is archived)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 204 },
    // TEMPLATES-T3/T4: an agent's delete held to its policy (Never: policy_denied; Ask: 409).
    errors: ["policy_denied", "conflict"],
    emits: ["task.deleted", "page.archived"],
  }),
  "tasks.move": defineRoute({
    ...core,
    id: "tasks.move",
    method: "post",
    path: taskSub("move"),
    tag: "tasks",
    summary: "Move a task to another board (status mapped or given)",
    access: "member",
    params: CompanyItemParams,
    body: TaskMove,
    response: { status: 200, schema: TaskWrite },
    // B2-GATES: a DE's move into a status its owner gates `ask` is 409 (propose it instead)
    errors: ["status_invalid", "task_locked", "conflict"],
    emits: ["task.moved", "task.gate_held"],
  }),
  "tasks.setStatus": defineRoute({
    ...core,
    id: "tasks.setStatus",
    method: "post",
    path: taskSub("status"),
    tag: "tasks",
    summary:
      "Change a task's status (hard dependencies block started/done statuses; an optional If-Match must be the task's version)",
    access: "member",
    params: CompanyItemParams,
    body: TaskStatusSet,
    response: { status: 200, schema: TaskWrite },
    errors: ["status_invalid", "dependency_blocked", "task_locked", "conflict"],
    // B2-GATES: a DE's move through its owner's `hold` gate
    emits: ["task.status_changed", "task.gate_held"],
  }),
  // TASK-LOCK: "Stop agent and move": an admin or manager, or the task's owner (its creator or a
  // person holding an assigned seat), cancels every live agent run on the task (`run.cancelled`
  // records who stopped it); the task is then free to move or reassign.
  "tasks.stopAgent": defineRoute({
    ...core,
    id: "tasks.stopAgent",
    method: "post",
    path: taskSub("stop-agent"),
    tag: "tasks",
    summary: "Stop the agent runs working on a task (admins, managers or the task's owner)",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: TaskWrite },
    errors: ["conflict"],
    emits: ["run.cancelled", "budget.settled"],
  }),
  // CARD-ARCHIVE (flag `settings.flags.card_archive`, off by default: 404 while off). A soft
  // archive: allowed for a person (or an agency operator, e.g. the ops automation account) who
  // edits the card's board, and for the card's creator seat. Archiving stops the agent runs on the
  // card (`run.cancelled` by a member; an agency operator's stop ends them `agent_stopped`), drops
  // its queued wake-ups and pending wake timers, closes its open loop incidents, and takes it out
  // of board views, agent board reads and wake dispatch. Unarchiving restores it and wakes no one.
  "tasks.archive": defineRoute({
    ...core,
    id: "tasks.archive",
    method: "post",
    path: taskSub("archive"),
    tag: "tasks",
    summary: "Archive a card (soft): stop its agent runs and wakes, hide it from board reads",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: CardArchiveState },
    errors: ["conflict"],
    emits: [
      "task.archived",
      "run.cancelled",
      "run.failed",
      "budget.settled",
      "budget.released",
      "wakeup.dropped",
      "loop_incident.resolved",
    ],
  }),
  "tasks.unarchive": defineRoute({
    ...core,
    id: "tasks.unarchive",
    method: "post",
    path: taskSub("unarchive"),
    tag: "tasks",
    summary: "Restore an archived card (nothing is woken by it)",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: CardArchiveState },
    errors: [],
    emits: ["task.unarchived"],
  }),
  "tasks.assign": defineRoute({
    ...core,
    id: "tasks.assign",
    method: "post",
    path: taskSub("assign"),
    tag: "tasks",
    summary: "Assign seats to a task",
    access: "member",
    params: CompanyItemParams,
    body: TaskAssign,
    response: { status: 200, schema: TaskAssignResult },
    // COS-SLICE1: the delegation guardrails (effects cap per run, three handoffs then park).
    // COS-MODES: an agent directing another person's agent is routed (agent-policy/direction.ts).
    errors: [
      "seat_closed",
      "effect_cap_reached",
      "handoff_parked",
      "task_locked",
      "policy_denied",
      "direction_refused",
      "conflict",
      // SHARED-DES: a shared DE takes work only from the scopes its links grant `task`.
      "not_in_reach",
    ],
    emits: [
      "task.assigned",
      "task.unassigned",
      "task.handed_off",
      "task.handoff_parked",
      "task.agent_directed",
      "approval.requested",
      "inbox_item.created",
    ],
  }),
  "tasks.agentWakes": defineRoute({
    ...core,
    id: "tasks.agentWakes",
    method: "get",
    path: taskSub("agent-wakes"),
    tag: "tasks",
    summary:
      "AGENT-MENTION: the digital employees the task is handed to (its DE seats) and what became of each handoff",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: TaskAgentWakes },
    errors: [],
    emits: [],
  }),
  "tasks.unassign": defineRoute({
    ...core,
    id: "tasks.unassign",
    method: "delete",
    path: taskSub("assignees/{seat_id}"),
    tag: "tasks",
    summary: "Unassign a seat",
    access: "member",
    params: TaskSeatParams,
    response: { status: 204 },
    errors: ["task_locked", ...AGENT_POLICY],
    emits: ["task.unassigned"],
  }),
  "tasks.setField": defineRoute({
    ...core,
    id: "tasks.setField",
    method: "put",
    path: taskSub("fields/{field_id}"),
    tag: "tasks",
    summary: "Set a field value (null clears it)",
    access: "member",
    params: TaskFieldParams,
    body: TaskFieldPut,
    response: { status: 200, schema: TaskFieldWrite },
    errors: [],
    emits: ["task.field_changed"],
  }),
  "tasks.checklist": defineRoute({
    ...core,
    id: "tasks.checklist",
    method: "get",
    path: taskSub("checklist"),
    tag: "tasks",
    summary: "A task's checklist",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: z.object({ data: z.array(TaskChecklistItem) }) },
    errors: [],
    emits: [],
  }),
  "tasks.addChecklistItem": defineRoute({
    ...core,
    id: "tasks.addChecklistItem",
    method: "post",
    path: taskSub("checklist"),
    tag: "tasks",
    summary: "Add a checklist item",
    access: "member",
    params: CompanyItemParams,
    body: ChecklistItemCreate,
    idempotency: "optional",
    response: { status: 201, schema: TaskChecklistItem },
    errors: AGENT_POLICY,
    emits: ["task.checklist_changed"],
  }),
  "tasks.updateChecklistItem": defineRoute({
    ...core,
    id: "tasks.updateChecklistItem",
    method: "patch",
    path: taskSub("checklist/{item_id}"),
    tag: "tasks",
    summary: "Tick, untick, rename or reorder a checklist item",
    access: "member",
    params: TaskSubParams,
    body: ChecklistItemPatch,
    response: { status: 200, schema: TaskChecklistItem },
    errors: AGENT_POLICY,
    emits: ["task.checklist_changed"],
  }),
  "tasks.deleteChecklistItem": defineRoute({
    ...core,
    id: "tasks.deleteChecklistItem",
    method: "delete",
    path: taskSub("checklist/{item_id}"),
    tag: "tasks",
    summary: "Remove a checklist item",
    access: "member",
    params: TaskSubParams,
    response: { status: 204 },
    errors: AGENT_POLICY,
    emits: ["task.checklist_changed"],
  }),
  // DOCS-KB: named checklists.
  "tasks.checklists": defineRoute({
    ...core,
    id: "tasks.checklists",
    method: "get",
    path: taskSub("checklists"),
    tag: "tasks",
    summary: "A task's checklists, each with its items in order",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: z.object({ data: z.array(TaskChecklistWithItems) }) },
    errors: [],
    emits: [],
  }),
  "tasks.createChecklist": defineRoute({
    ...core,
    id: "tasks.createChecklist",
    method: "post",
    path: taskSub("checklists"),
    tag: "tasks",
    summary: "Add a checklist to a task (with its first items, optionally)",
    access: "member",
    params: CompanyItemParams,
    body: ChecklistCreate,
    idempotency: "optional",
    response: { status: 201, schema: TaskChecklistWithItems },
    errors: AGENT_POLICY,
    emits: ["task.checklist_changed"],
  }),
  "tasks.updateChecklist": defineRoute({
    ...core,
    id: "tasks.updateChecklist",
    method: "patch",
    path: taskSub("checklists/{checklist_id}"),
    tag: "tasks",
    summary: "Rename or reorder a checklist",
    access: "member",
    params: TaskChecklistParams,
    body: ChecklistPatch,
    response: { status: 200, schema: TaskChecklistWithItems },
    errors: AGENT_POLICY,
    emits: ["task.checklist_changed"],
  }),
  "tasks.deleteChecklist": defineRoute({
    ...core,
    id: "tasks.deleteChecklist",
    method: "delete",
    path: taskSub("checklists/{checklist_id}"),
    tag: "tasks",
    summary: "Delete a checklist with its items",
    access: "member",
    params: TaskChecklistParams,
    response: { status: 204 },
    errors: AGENT_POLICY,
    emits: ["task.checklist_changed"],
  }),
  "tasks.reorderChecklist": defineRoute({
    ...core,
    id: "tasks.reorderChecklist",
    method: "post",
    path: taskSub("checklists/{checklist_id}/reorder"),
    tag: "tasks",
    summary: "Put a checklist's items in the given order (drag to reorder)",
    access: "member",
    params: TaskChecklistParams,
    body: ChecklistReorder,
    response: { status: 200, schema: TaskChecklistWithItems },
    errors: AGENT_POLICY,
    emits: ["task.checklist_changed"],
  }),
  "tasks.dependencies": defineRoute({
    ...core,
    id: "tasks.dependencies",
    method: "get",
    path: taskSub("dependencies"),
    tag: "tasks",
    summary: "What a task depends on",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: z.object({ data: z.array(TaskDependency) }) },
    errors: [],
    emits: [],
  }),
  "tasks.addDependency": defineRoute({
    ...core,
    id: "tasks.addDependency",
    method: "post",
    path: taskSub("dependencies"),
    tag: "tasks",
    summary: "Add a hard or soft dependency (no cycles)",
    access: "member",
    params: CompanyItemParams,
    body: DependencyAdd,
    response: { status: 201, schema: TaskDependency },
    errors: ["conflict", "dependency_cycle", "policy_denied"],
    emits: ["task.dependency_changed"],
  }),
  "tasks.removeDependency": defineRoute({
    ...core,
    id: "tasks.removeDependency",
    method: "delete",
    path: taskSub("dependencies/{item_id}"),
    tag: "tasks",
    summary: "Remove a dependency (`item_id` is the depended-on task)",
    access: "member",
    params: TaskSubParams,
    response: { status: 204 },
    errors: AGENT_POLICY,
    emits: ["task.dependency_changed"],
  }),
  "tasks.thread": defineRoute({
    ...core,
    id: "tasks.thread",
    method: "get",
    path: taskSub("thread"),
    tag: "tasks",
    summary: "The task's thread id",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: TaskThread },
    errors: [],
    emits: [],
  }),
  "tasks.history": defineRoute({
    ...core,
    id: "tasks.history",
    method: "get",
    path: taskSub("history"),
    tag: "tasks",
    summary: "Events of this task, newest first",
    access: "member",
    params: CompanyItemParams,
    query: PageQuery,
    response: { status: 200, schema: TaskHistory },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "tasks.bulk": defineRoute({
    ...core,
    id: "tasks.bulk",
    method: "post",
    path: `${base}/tasks/bulk`,
    tag: "tasks",
    summary: "Apply one action to many tasks (per-task result; each change is its own event)",
    access: "member",
    params: CompanyParams,
    body: TaskBulk,
    idempotency: "optional",
    response: { status: 200, schema: TaskBulkResult },
    errors: ["task_locked", ...AGENT_POLICY],
    emits: [
      "task.status_changed",
      "task.assigned",
      "task.moved",
      "task.field_changed",
      "task.deleted",
      "page.archived",
      "task.tagged",
      "task.untagged",
      // B2-GATES: a DE's status through its owner's `hold` gate
      "task.gate_held",
    ],
  }),
  "tasks.search": defineRoute({
    ...core,
    id: "tasks.search",
    method: "post",
    path: `${base}/tasks/search`,
    tag: "tasks",
    summary: "Full-text and field search over tasks the caller can view",
    access: "member",
    params: CompanyParams,
    body: TaskSearch,
    safe: true,
    response: { status: 200, schema: page(TaskSearchHit) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "tasks.propose": defineRoute({
    ...core,
    id: "tasks.propose",
    method: "post",
    path: taskSub("propose"),
    tag: "tasks",
    summary: "Submit a change to a task as an approval instead of applying it",
    access: "member",
    params: CompanyItemParams,
    body: TaskPropose,
    idempotency: "optional",
    response: { status: 201, schema: Approval },
    errors: ["status_invalid", "supervisor_required"],
    emits: ["approval.requested"],
  }),
  // BOARD-GATES (§2.2): a column only a person moves cards out of — its buttons, and the decision.
  "tasks.reviewGate": defineRoute({
    ...core,
    id: "tasks.reviewGate",
    method: "get",
    path: taskSub("review"),
    tag: "tasks",
    summary:
      "The person-only gate of the task's column (Approve · Rework · Reject targets, who decides), or null",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: TaskReviewGate },
    errors: [],
    emits: [],
  }),
  "tasks.review": defineRoute({
    ...core,
    id: "tasks.review",
    method: "post",
    path: taskSub("review"),
    tag: "tasks",
    summary:
      "Approve, Rework or Reject a task in a person-only column (its approver): the task moves to the column's target; a rework or reject note is posted",
    access: "member",
    params: CompanyItemParams,
    body: TaskReviewDecide,
    idempotency: "optional",
    response: { status: 200, schema: TaskWrite },
    errors: ["forbidden", "conflict", "required_missing", "task_locked", "dependency_blocked"],
    emits: ["task.status_changed", "message.created", "task.updated", "task.field_changed"],
  }),
  // B2-GATES: a DE's move through a `hold` gate, as its owner sees it, and the pull-back.
  "tasks.hold": defineRoute({
    ...core,
    id: "tasks.hold",
    method: "get",
    path: taskSub("hold"),
    tag: "tasks",
    summary: "The task's latest held move (a DE's move through a `hold` gate), or null",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: TaskHold },
    errors: [],
    emits: [],
  }),
  "tasks.pullHold": defineRoute({
    ...core,
    id: "tasks.pullHold",
    method: "post",
    path: taskSub("hold/pull"),
    tag: "tasks",
    summary:
      "Pull a held move back (the task's owner, within the hold): the task returns, the note is posted and the DE woken with it",
    access: "member",
    params: CompanyItemParams,
    body: GateHoldPull,
    idempotency: "optional",
    response: { status: 200, schema: TaskHold },
    errors: ["forbidden", "conflict"],
    emits: [
      "task.gate_pulled",
      "task.status_changed",
      // B2-FIXES: a held move from another board goes back to that board
      "task.moved",
      "message.created",
      "wakeup.requested",
      "inbox_item.created",
    ],
  }),
  "tasks.import": defineRoute({
    ...core,
    id: "tasks.import",
    method: "post",
    path: `${base}/boards/{id}/tasks/import`,
    tag: "tasks",
    summary:
      "CSV import of tasks onto a board with a column mapping (dry_run supported; M2-08). Board editors",
    access: "member",
    params: CompanyItemParams,
    body: TaskImportUpload,
    bodyType: "multipart",
    idempotency: "optional",
    response: { status: 200, schema: ImportResult },
    errors: ["payload_too_large"],
    emits: ["task.created", "import.committed"],
  }),
  "my.tasks": defineRoute({
    ...core,
    id: "my.tasks",
    method: "get",
    path: `${base}/my/tasks`,
    tag: "tasks",
    summary: "Tasks assigned to the caller's seats, across boards",
    access: "member",
    params: CompanyParams,
    query: MyTasksQuery,
    response: { status: 200, schema: page(TaskCard) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
} as const;
